import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import repeatIcon from "@/assets/Icons/Repeat.png";
import chatIcon from "@/assets/Icons/Chat1.png";
import callIcon from "@/assets/Icons/Call.png";
import mailIcon from "@/assets/Icons/Mail.png";
import { Logo } from "./Logo";
import ContactDialog from "./ContactDialog";
import { useState } from "react";
import MobileNav from "./MobileNav";

const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const { user, isAdmin, signOut } = useAuth();
  const [contactOpen, setContactOpen] = useState(false);

  const products = language === "is" 
    ? [
        { name: "FlæðiFulltrúi", href: "/is/products/automation", icon: repeatIcon },
        { name: "SpjallFulltrúi", href: "/is/products/chat", icon: chatIcon },
        { name: "RaddFulltrúi", href: "/is/products/voice", icon: callIcon },
        { name: "PóstFulltrúi", href: "/is/products/email", icon: mailIcon },
      ]
    : [
        { name: "Automation Fulltrúi", href: "/en/products/automation", icon: repeatIcon },
        { name: "Chat Fulltrúi", href: "/en/products/chat", icon: chatIcon },
        { name: "Voice Fulltrúi", href: "/en/products/voice", icon: callIcon },
        { name: "Email Fulltrúi", href: "/en/products/email", icon: mailIcon },
      ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo and Name */}
        <a href={`/${language}`} className="hover:opacity-80 transition-opacity">
          <Logo size="md" layout="horizontal" />
        </a>

        {/* Navigation Menu */}
        <div className="hidden md:flex items-center gap-2">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className="inline-flex h-10 items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                >
                  {t.nav.products}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="p-2 md:w-64 bg-background space-y-1">
                    {products.map((product) => (
                      <li key={product.name}>
                        <NavigationMenuLink asChild>  
                          <a
                            href={product.href}
                            className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted"
                          >
                            <img  
                              src={product.icon}
                              alt={product.name}
                              className="h-8 w-8 object-contain"
                            />
                            <span>{product.name}</span>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

            <NavigationMenuItem>
              <button
                onClick={() => setContactOpen(true)}
                className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted focus:outline-none disabled:pointer-events-none disabled:opacity-50"
              >
                {t.nav.contact}
              </button>
            </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* About Link */}
          <a
            href={`/${language}/about`}
            className="inline-flex h-10 items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted"
          >
            {t.nav.about}
          </a>
        </div>

        {/* Right Side: Language Selector and Login/User Menu */}
        <div className="flex items-center gap-3">
          <MobileNav onContactOpen={() => setContactOpen(true)} />
          
          {/* Language Toggle Button */}
          <div className="inline-flex rounded-md border border-input bg-background p-1 gap-1">
            <button
              onClick={() => setLanguage("is")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors rounded ${
                language === "is"
                  ? "bg-muted text-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              IS
            </button>
            <button
              onClick={() => setLanguage("en")}
              className={`px-3 py-1.5 text-sm font-medium transition-colors rounded ${
                language === "en"
                  ? "bg-muted text-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              EN
            </button>
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  {user.email?.[0]?.toUpperCase() || "U"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user.user_metadata?.full_name || "User"}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  {language === "is" ? "Prófíll" : "Profile"}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => window.location.href = `/${language}/admin`}>
                    <Shield className="mr-2 h-4 w-4" />
                    {language === "is" ? "Stjórnborð" : "Admin Panel"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {language === "is" ? "Útskrá" : "Sign out"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            null
          )}
        </div>
      </div>
      
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </nav>
  );
};

export default Navigation;
